/**
 * Application route definitions.
 *
 * Extracted from App.tsx to keep the main entry point focused on providers and layout.
 */
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useMenuSettings } from './contexts/MenuSettingsContext';
import { isOnboardingPageHidden } from './config/navigation';
import MainLayout from './components/layout/MainLayout';
import NavigationAccessGate from './components/layout/NavigationAccessGate';
import { PageErrorBoundary } from './components/common';
import { Loader2 } from 'lucide-react';

// Page imports
import Login from './pages/Login';
import MfaSetup from './pages/MfaSetup';
import MfaVerify from './pages/MfaVerify';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import { CompanyRegistrationForm } from './components/registration/CompanyRegistrationForm';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Suppliers from './pages/Suppliers';
import Customers from './pages/Customers';
import SupplierDetail from './pages/SupplierDetail';
// CR-6247: click-through profile for a company in Organisation Structure.
import CompanyProfile from './pages/CompanyProfile';
import SupplierGroupDetail from './pages/SupplierGroupDetail';
import SupplierPerformance from './pages/SupplierPerformance';
import SupplierOnboardingWizard from './pages/suppliers/SupplierOnboardingWizard';
import SupplierApprovalWizard from './pages/suppliers/SupplierApprovalWizard';
import SupplierQuestionnaires from './pages/suppliers/SupplierQuestionnaires';
import Settings from './pages/Settings';
import AdminSettings from './pages/AdminSettings';
import AdminSetup from './pages/AdminSetup';
import PlatformAdminPage from './pages/admin/PlatformAdminPage';
import ImportDashboard from './pages/admin/ImportDashboard';
import EmailQueueDashboard from './pages/admin/EmailQueueDashboard';
import ExchangeRatesPage from './pages/admin/ExchangeRatesPage';
import EmailTemplatePreview from './pages/admin/EmailTemplatePreview';
import UsersManagement from './pages/admin/UsersManagement';
import GrnAllocationRepair from './pages/admin/GrnAllocationRepair';
import IntegrationsSettings from './pages/admin/IntegrationsSettings';
import PlatformAnalytics from './pages/admin/PlatformAnalytics';
import Purchasing from './pages/Purchasing';
import PartsMaster from './pages/PartsMaster';
import PartSchedulePage from './pages/PartSchedule';
import Quality from './pages/Quality';
import NCREdit from './pages/NCREdit';
// DORMANT -- not wired since 30 Sep 2026 (QUALITY-NCR-REVIEW: the NCR page offers the server PDF).
// import NCRPrint from './pages/NCRPrint';
import NCRCreate from './pages/NCRCreate';
import Concessions from './pages/Concessions';
import ConcessionDetail from './pages/ConcessionDetail';
import ApprovalRegister from './pages/ApprovalRegister';
import MyCollections from './pages/MyCollections';
import RFQ from './pages/RFQ';
import RFQDetail from './pages/RFQDetail';
import Documents from './pages/Documents';
import SupplierIntelligence from './pages/SupplierIntelligence';
import InviteUser from './pages/InviteUser';
import UserInvitations from './pages/UserInvitations';
import AcceptInvitation from './pages/AcceptInvitation';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import NewsForm from './pages/NewsForm';
import UserActivity from './pages/UserActivity';
import QualificationTemplates from './pages/QualificationTemplates';
import TemplateBuilder from './pages/TemplateBuilder';
import SupplierQualifications from './pages/SupplierQualifications';
import Questionnaires from './pages/Questionnaires';
import QualificationDetail from './pages/QualificationDetail';
import AuditDetail from './pages/AuditDetail';
import Notifications from './pages/Notifications';
import { OnboardingDashboard, NewInvitation, DatasetsList, DatasetBuilderPage, AcceptSupplierInvitation, FillQuestionnaire, ReviewQuestionnaire, MyOnboarding, InvitationsList } from './pages/onboarding';
import { CustomerPortalDashboard, CustomersList, NewCustomerInvitation, CustomerDetail, CustomerOrders, CustomerDatasets } from './pages/customer-portal';
import Help from './pages/Help';
import Reports from './pages/Reports';
import PublicDashboard from './pages/PublicDashboard';
import DeliveryAppeals from './pages/DeliveryAppeals';
import SpendLeakage from './pages/SpendLeakage';
import SpendLeakageDetail from './pages/SpendLeakageDetail';
import BulkUpload from './pages/BulkUpload';
import DataImport from './pages/DataImport';
import StockLevelsPage from './pages/StockLevelsPage';
import LogisticsCallOffs from './pages/LogisticsCallOffs';
import {
  SpendData as BarrattSpendData,
  DivisionPerformance as BarrattDivisionPerformance,
  DivisionScoreReport as BarrattDivisionScoreReport,
  CallOffPerformance as BarrattCallOffPerformance,
  SupplierUploadHistory as BarrattSupplierUploadHistory,
  Analyse as BarrattAnalyse,
  Category as BarrattCategory,
  DataUpload as BarrattDataUpload,
} from './pages/barratt';

/**
 * CR-5919: Paths a pre-approval supplier (supplier_is_approved === false) is
 * allowed to reach. Anything outside this list is redirected back to
 * /onboarding/my-progress. /settings and /help stay accessible because the
 * supplier sidebar footer still surfaces them, and suppliers need to be able
 * to manage their own credentials while completing onboarding.
 *
 * CR-5964: /intelligence/qualifications is allowed because the My Onboarding
 * "Start" button routes pre-approval suppliers to the qualification detail
 * page to fill out their assigned questionnaires — without this, the
 * pre-approval gate redirects them straight back to /onboarding/my-progress
 * and the Start button looks like a dead click.
 */
const PRE_APPROVAL_SUPPLIER_ALLOWED_PREFIXES = [
  '/onboarding',
  '/settings',
  '/help',
  '/notifications',
  '/terms',
  '/privacy',
  '/intelligence/qualifications',
];

function isPathAllowedForPreApprovalSupplier(pathname: string): boolean {
  return PRE_APPROVAL_SUPPLIER_ALLOWED_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(prefix + '/')
  );
}

/**
 * Protected Route wrapper - redirects to login if not authenticated.
 *
 * CR-5919: Also gates pre-approval supplier users (supplier_is_approved === false)
 * away from any path outside the onboarding allowlist — they should only be
 * able to reach their questionnaires until the customer approves them.
 */
export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // CR-5887: remember where the user was headed so Login can send them
    // back there after authenticating (e.g. Daily Quality Summary email
    // deep-links) instead of always dumping them on /dashboard.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isPreApprovalSupplier =
    user?.company_type === 'supplier' && user?.supplier_is_approved === false;
  if (isPreApprovalSupplier && !isPathAllowedForPreApprovalSupplier(location.pathname)) {
    return <Navigate to="/onboarding/my-progress" replace />;
  }

  return <>{children}</>;
};

/**
 * Route /onboarding straight to /onboarding/my-progress for supplier users —
 * the legacy OnboardingDashboard is customer-facing and presents the wrong
 * frame when a supplier lands there.
 *
 * CR-7478: a customer tenant with the Compliance dashboard tab switched off
 * has no Supplier Onboarding page, so a direct visit goes to the dashboard,
 * using the same rule that hides the menu item.
 */
function SupplierAwareOnboarding() {
  const { user } = useAuth();
  const { isDashboardTabEnabled, isComplianceMode, isLoading } = useMenuSettings();
  // Wait for the FIRST settings load only: a site switch reloads the settings
  // too, and blanking the page then would remount it and lose its state.
  const [settingsReady, setSettingsReady] = React.useState(!isLoading);
  React.useEffect(() => { if (!isLoading) setSettingsReady(true); }, [isLoading]);
  if (user?.company_type === 'supplier') {
    return <Navigate to="/onboarding/my-progress" replace />;
  }
  if (!settingsReady) return null;
  if (isOnboardingPageHidden(isDashboardTabEnabled, isComplianceMode)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <OnboardingDashboard />;
}

/**
 * Smart home redirect. Logistics users get their call-offs page, pre-approval
 * suppliers go to /onboarding/my-progress, approved suppliers use the
 * platform-configured landing path, everyone else goes to the dashboard.
 */
function HomeRedirect() {
  const { user } = useAuth();
  const { supplierDefaultLandingPath, isLoading: settingsLoading } = useMenuSettings();
  if (user?.company_type === 'logistics') {
    return <Navigate to="/logistics/call-offs" replace />;
  }
  if (user?.company_type === 'supplier') {
    // Wait for platform settings before routing — otherwise we redirect to
    // /dashboard first and only apply the configured path on next render.
    if (settingsLoading) return null;
    const dest = user?.supplier_is_approved === false
      ? '/onboarding/my-progress'
      : (supplierDefaultLandingPath || '/dashboard');
    return <Navigate to={dest} replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

/**
 * Helper to wrap a page component with ProtectedRoute + MainLayout +
 * NavigationAccessGate + PageErrorBoundary.
 *
 * CR-7058: NavigationAccessGate refuses pages whose navigation entry has been
 * switched off in Platform Admin → Navigation Menu Management, so the toggle
 * is an access control rather than a sidebar cosmetic.
 */
function protectedPage(pageName: string, element: React.ReactNode) {
  return (
    <ProtectedRoute>
      <MainLayout>
        <NavigationAccessGate>
          <PageErrorBoundary pageName={pageName}>
            {element}
          </PageErrorBoundary>
        </NavigationAccessGate>
      </MainLayout>
    </ProtectedRoute>
  );
}

/**
 * All application routes.
 */
export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      {/* CR-5275: TOTP MFA step pages — public, gated by session token in state. */}
      <Route path="/mfa-setup" element={<MfaSetup />} />
      <Route path="/mfa-verify" element={<MfaVerify />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/register" element={<CompanyRegistrationForm />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/onboarding/accept/:token" element={<AcceptSupplierInvitation />} />
      <Route path="/public/dashboard/:token" element={<PublicDashboard />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={protectedPage("Dashboard", <Dashboard />)} />
      <Route path="/calendar" element={protectedPage("Calendar", <Calendar />)} />
      <Route path="/suppliers" element={protectedPage("Suppliers", <Suppliers />)} />
      <Route path="/customers" element={protectedPage("Customers", <Customers />)} />

      {/* Static and multi-segment supplier paths before /suppliers/:id */}
      <Route path="/suppliers/groups/:groupId" element={protectedPage("Supplier Group Detail", <SupplierGroupDetail />)} />
      <Route path="/suppliers/performance" element={protectedPage("Supplier Performance", <SupplierPerformance />)} />
      <Route path="/suppliers/wizard/:id" element={protectedPage("Supplier Onboarding Wizard", <SupplierOnboardingWizard />)} />
      <Route path="/suppliers/:id/approval" element={protectedPage("Supplier Approval", <SupplierApprovalWizard />)} />
      <Route path="/suppliers/:id/questionnaires" element={protectedPage("Supplier Questionnaires", <SupplierQuestionnaires />)} />
      <Route path="/suppliers/:id" element={protectedPage("Supplier Detail", <SupplierDetail />)} />

      <Route path="/purchasing" element={protectedPage("Purchasing", <Purchasing />)} />
      <Route path="/parts-master" element={protectedPage("Parts Master", <PartsMaster />)} />
      <Route path="/part-schedule" element={protectedPage("Part Schedule", <PartSchedulePage />)} />

      {/* Quality */}
      <Route path="/quality" element={protectedPage("Quality", <Quality />)} />
      <Route path="/quality/ncr/new" element={protectedPage("NCR Create", <NCRCreate />)} />
      <Route path="/quality/ncr/:id/edit" element={protectedPage("NCR Edit", <NCREdit />)} />
      {/* DORMANT -- the Print NCR route is not wired since 30 Sep 2026 (the NCR page offers the server PDF); see QUALITY-NCR-REVIEW.
      <Route path="/quality/ncr/:id/print" element={<ProtectedRoute><PageErrorBoundary pageName="NCR Print"><NCRPrint /></PageErrorBoundary></ProtectedRoute>} /> */}
      <Route path="/concessions" element={protectedPage("Concessions", <Concessions />)} />
      <Route path="/quality/concession/:id" element={protectedPage("Concession Detail", <ConcessionDetail />)} />
      {/* CR-5534: Quality Approval Register (Suppliers × Accreditations) */}
      <Route path="/quality/approval-register" element={protectedPage("Approval Register", <ApprovalRegister />)} />
      {/* CR-7352: personal queue of collections assigned to this user */}
      <Route path="/quality/my-collections" element={protectedPage("My Collections", <MyCollections />)} />

      {/* RFQ */}
      <Route path="/rfq" element={protectedPage("RFQ", <RFQ />)} />
      <Route path="/rfq/:id" element={protectedPage("RFQ Detail", <RFQDetail />)} />

      {/* Documents & Intelligence */}
      <Route path="/documents" element={protectedPage("Documents", <Documents />)} />
      <Route path="/supplier-intelligence" element={protectedPage("Supplier Intelligence", <SupplierIntelligence />)} />
      <Route path="/user-activity" element={protectedPage("User Activity", <UserActivity />)} />

      {/* Questionnaires - Unified view with Templates and Supplier Responses */}
      <Route path="/questionnaires" element={protectedPage("Questionnaires", <Questionnaires />)} />
      <Route path="/intelligence/templates" element={protectedPage("Qualification Templates", <QualificationTemplates />)} />
      <Route path="/intelligence/templates/new" element={protectedPage("Template Builder", <TemplateBuilder />)} />
      <Route path="/intelligence/templates/:id" element={protectedPage("Template Builder", <TemplateBuilder />)} />
      <Route path="/intelligence/qualifications" element={protectedPage("Supplier Qualifications", <SupplierQualifications />)} />
      <Route path="/intelligence/qualifications/:id" element={protectedPage("Qualification Detail", <QualificationDetail />)} />
      <Route path="/audits/:id" element={protectedPage("Audit", <AuditDetail />)} />
      <Route path="/questionnaire" element={<Navigate to="/questionnaires" replace />} />

      {/* News */}
      <Route path="/news" element={protectedPage("News", <News />)} />
      <Route path="/news/create" element={protectedPage("Create News", <NewsForm />)} />
      <Route path="/news/edit/:id" element={protectedPage("Edit News", <NewsForm />)} />
      <Route path="/news/:id" element={protectedPage("News Detail", <NewsDetail />)} />

      {/* Admin */}
      <Route path="/admin" element={protectedPage("Admin Settings", <AdminSettings />)} />
      <Route path="/admin/setup" element={protectedPage("Admin Setup", <AdminSetup />)} />
      {/* CR-6247: Company Profile, opened from Admin Setup > Organisation Structure */}
      <Route path="/companies/:id" element={protectedPage("Company Profile", <CompanyProfile />)} />
      <Route path="/admin/platform" element={protectedPage("Platform Admin", <PlatformAdminPage />)} />
      {/* CR-6273: standalone /admin/login-as removed. Impersonation is reached
          from User Management → User details → Login As only. */}
      <Route path="/admin/users" element={protectedPage("Users Management", <UsersManagement />)} />
      <Route path="/admin/grn-repair" element={protectedPage("GRN Allocation Repair", <GrnAllocationRepair />)} />
      <Route path="/admin/integrations" element={protectedPage("Integrations Settings", <IntegrationsSettings />)} />
      <Route path="/admin/email-queue" element={protectedPage("Email Queue", <EmailQueueDashboard />)} />
      <Route path="/admin/exchange-rates" element={protectedPage("Exchange Rates", <ExchangeRatesPage />)} />
      <Route path="/admin/email-templates" element={protectedPage("Email Templates", <EmailTemplatePreview />)} />
      <Route path="/admin/platform-analytics" element={protectedPage("Platform Analytics", <PlatformAnalytics />)} />
      <Route path="/import-monitor" element={protectedPage("Import Monitor", <ImportDashboard />)} />

      {/* Reports */}
      <Route path="/reports" element={protectedPage("Reports", <Reports />)} />

      {/* Stock Levels */}
      <Route path="/stock-levels" element={protectedPage("Stock Levels", <StockLevelsPage />)} />

      {/* Barratt Features */}
      <Route path="/delivery-appeals/:id" element={protectedPage("Delivery Appeal Detail", <DeliveryAppeals />)} />
          <Route path="/delivery-appeals" element={protectedPage("Delivery Appeals", <DeliveryAppeals />)} />
          <Route path="/spend-leakage/:id" element={protectedPage("Spend Leakage Detail", <SpendLeakageDetail />)} />
      <Route path="/spend-leakage" element={protectedPage("Spend Leakage", <SpendLeakage />)} />
      <Route path="/data-import" element={protectedPage("Data Import", <DataImport />)} />
      <Route path="/bulk-upload" element={protectedPage("Bulk Upload", <BulkUpload />)} />

      {/* Barratt-specific pages */}
      <Route path="/barratt/spend-data" element={protectedPage("Spend Data", <BarrattSpendData />)} />
      <Route path="/barratt/division-performance" element={protectedPage("Division Performance", <BarrattDivisionPerformance />)} />
      <Route path="/barratt/division-score-report" element={protectedPage("Division Score Report", <BarrattDivisionScoreReport />)} />
      <Route path="/barratt/call-off-performance" element={protectedPage("Call Off Performance", <BarrattCallOffPerformance />)} />
      <Route path="/barratt/supplier-upload-history" element={protectedPage("Supplier Upload History", <BarrattSupplierUploadHistory />)} />
      <Route path="/barratt/analyse" element={protectedPage("Analyse", <BarrattAnalyse />)} />
      <Route path="/barratt/category" element={protectedPage("Category", <BarrattCategory />)} />
      <Route path="/barratt/data-upload" element={protectedPage("Data Upload", <BarrattDataUpload />)} />

      {/* Users */}
      <Route path="/users/invitations" element={protectedPage("User Invitations", <UserInvitations />)} />
      <Route path="/users/invite" element={protectedPage("Invite User", <InviteUser />)} />

      {/* Settings & Notifications */}
      <Route path="/settings" element={protectedPage("Settings", <Settings />)} />
      <Route path="/notifications" element={protectedPage("Notifications", <Notifications />)} />

      {/* Onboarding routes */}
      <Route path="/onboarding" element={protectedPage("Onboarding Dashboard", <SupplierAwareOnboarding />)} />
      <Route path="/onboarding/invitations" element={protectedPage("Invitations", <InvitationsList />)} />
      <Route path="/onboarding/invitations/new" element={protectedPage("New Invitation", <NewInvitation />)} />
      <Route path="/onboarding/datasets" element={protectedPage("Datasets", <DatasetsList />)} />
      <Route path="/onboarding/datasets/new" element={protectedPage("New Dataset", <DatasetBuilderPage />)} />
      <Route path="/onboarding/datasets/:id" element={protectedPage("Dataset Builder", <DatasetBuilderPage />)} />
      {/* CR-4788: Existing 'Onboarding Submitted' notifications link to
          /onboarding/workflows/{id} — a workflow-metadata view that hides
          the submitted questionnaires and offers no way to actually review
          them. Redirect to the Pending Due Diligence tab where reviewers
          can see + action the questionnaires. New notifications generated
          after the services.py fix already target the correct URL, but
          this keeps older notifications functional too. */}
      <Route path="/onboarding/workflows/:id" element={<Navigate to="/onboarding?tab=reviews" replace />} />
      <Route path="/onboarding/fill/:id" element={protectedPage("Fill Questionnaire", <FillQuestionnaire />)} />
      <Route path="/onboarding/review/:id" element={protectedPage("Review Questionnaire", <ReviewQuestionnaire />)} />
      <Route path="/onboarding/my-progress" element={protectedPage("My Onboarding", <MyOnboarding />)} />

      {/* Customer Portal Mode routes */}
      <Route path="/customer-portal" element={protectedPage("Customer Portal Dashboard", <CustomerPortalDashboard />)} />
      <Route path="/customer-portal/customers" element={protectedPage("Customers List", <CustomersList />)} />
      <Route path="/customer-portal/customers/:id" element={protectedPage("Customer Detail", <CustomerDetail />)} />
      <Route path="/customer-portal/invite" element={protectedPage("Invite Customer", <NewCustomerInvitation />)} />
      <Route path="/customer-portal/orders" element={protectedPage("Customer Orders", <CustomerOrders />)} />
      <Route path="/customer-portal/datasets" element={protectedPage("Customer Questionnaires", <CustomerDatasets />)} />

      {/* Logistics */}
      <Route path="/logistics/call-offs" element={protectedPage("Logistics Call-Offs", <LogisticsCallOffs />)} />

      {/* Help */}
      <Route path="/help" element={protectedPage("Help", <Help />)} />

      {/* Redirects */}
      <Route path="/profile" element={<Navigate to="/settings" replace />} />
      <Route path="/" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
      <Route path="*" element={<ProtectedRoute><HomeRedirect /></ProtectedRoute>} />
    </Routes>
  );
}
